import { ensureRouteData, prefetchRouteData } from '@/app/query-client'
import {
  brokerDetailQueryOptions,
  brokerListQueryOptions,
  brokerPeersQueryOptions,
  brokerStocksQueryOptions,
} from '@/features/brokers/api/queries'
import { rememberLastBroker } from '@/features/brokers/lib/lastUsed'
import type { RouterContext } from '@/routes/layouts/root'
import { BROKER_DIRECTORY_QUERY } from '@/features/brokers/lib/brokerDirectory'
import { brokerSearchSchema, toBrokerStocksQuery } from '@/screens/brokers/search'

/**
 * The `/brokers/$code` loader.
 *
 * MAIN, but keyed on the `period` search param (`brokerSearchSchema`), so
 * this awaits only the ENTRY URL's period — a period click afterwards is a
 * search-only change that reruns no loader, and `BrokerScreen` keeps reading
 * it with `useBrokerDetail`'s own `placeholderData: keepPreviousData`, not a
 * suspense hook (docs/route-loading.md, "Search params"). One payload feeds
 * six blocks (header, KPI tiles, performance card, the three ranked tables,
 * vs-market, holdings), so awaiting it once here is what keeps every one of
 * them off its own skeleton on the first paint.
 *
 * SECONDARY, started and not awaited, and only on a real navigation
 * (docs/route-loading.md, "Secondary reads"): the picker's broker list
 * (needs nothing from the detail payload), the stocks table for the entry
 * URL's search/sort/page (its own `BrokerStockActivityCard` draws the
 * skeleton and error), and the peers strip. All three read a DIFFERENT key
 * from `detail` — see keys.ts — so none of them can go stale together with
 * it. Skipped on a hover-intent `preload` so a cursor crossing a list of
 * broker links does not fire three extra requests per row; the MAIN read
 * below still starts on hover.
 *
 * `BROKER_NOT_FOUND` resolves to `null` rather than throwing
 * (docs/route-loading.md, "Not-found answers" — see
 * `brokerDetailQueryOptions`'s own comment), so this loader settles
 * normally even for a bad code, and `BrokerScreen` keeps
 * its own `data === null` branch — the same shape Stock's `company === null`
 * uses.
 *
 * Remembers the code as last-used only for a real result — a bad code must
 * not overwrite a good remembered broker — and only for a real navigation
 * (`!preload`): the router runs this loader on a hover too, and a cursor
 * merely crossing a broker link must not overwrite what the member last
 * actually opened. Reviews' own loader prefetches this same detail query,
 * unawaited, for its header's broker name.
 *
 * Its own module, as `app.guard.ts` is, so a test can mount it under a root
 * of its own: typed inside `createFileRoute`, a loader is bound to this
 * route's parent in the generated tree. It imports from `api/`, `lib/` and
 * schema modules only — route loaders are not code-split, so anything
 * imported here ships in the entry chunk.
 */
export async function loadBroker({
  context: { queryClient },
  params,
  location,
  preload,
}: {
  context: RouterContext
  params: { code: string }
  location: { search: unknown }
  preload: boolean
}): Promise<void> {
  const search = brokerSearchSchema.parse(location.search)
  if (!preload) {
    prefetchRouteData(queryClient, brokerListQueryOptions(BROKER_DIRECTORY_QUERY))
    prefetchRouteData(
      queryClient,
      brokerStocksQueryOptions(params.code, toBrokerStocksQuery(search)),
    )
    prefetchRouteData(queryClient, brokerPeersQueryOptions(params.code))
  }
  const result = await ensureRouteData(
    queryClient,
    brokerDetailQueryOptions(params.code, search.period),
  )
  if (!preload && result !== null) rememberLastBroker(params.code)
}
